<template>
  <v-main>
    <v-container fluid class="pa-6">
      <!-- Step 1: Visibility score entry + existing brands -->
      <template v-if="currentStep === 1">
        <div class="dashboard-home">
          <div class="dashboard-home__logo">
            <img :src="inletsLogo" alt="inlets.ai" class="brand-logo-img" />
          </div>

          <v-card variant="flat" class="pa-8 pa-md-10 score-entry-card">
            <div class="score-entry-inner">
              <div class="text-center mb-8">
                <span class="brand-eyebrow score-entry-eyebrow">Visibility check</span>
                <h1 class="score-entry-heading brand-heading mb-3">
                  What's your <span class="score-entry-brand">Visibility</span> score?
                </h1>
                <p class="score-entry-subtext mb-0">
                  See how visible your clinic is when people ask AI where to go.
                </p>
              </div>

              <v-form ref="brandForm" @submit.prevent="nextStep" class="score-entry-form">
                <v-text-field
                  v-model="brandName"
                  placeholder="Search your healthcare organization"
                  prepend-inner-icon="mdi-magnify"
                  variant="outlined"
                  hide-details="auto"
                  :rules="[(v) => !!v || 'Brand name is required']"
                  class="score-entry-field mb-2"
                  :disabled="freePlanBrandLimitPending"
                  required
                ></v-text-field>

                <v-expand-transition>
                  <div v-if="showWebsiteField" class="score-entry-reveal">
                    <v-text-field
                      v-model="brandUrl"
                      label="Website URL"
                      placeholder="example.com"
                      prepend-inner-icon="mdi-link"
                      variant="outlined"
                      hide-details="auto"
                      :rules="urlRules"
                      class="score-entry-field mb-2"
                      required
                    ></v-text-field>
                  </div>
                </v-expand-transition>

                <v-expand-transition>
                  <div v-if="showRegionField" class="score-entry-reveal">
                    <v-select
                      v-model="selectedCountry"
                      label="Region"
                      placeholder="Select region"
                      prepend-inner-icon="mdi-earth"
                      variant="outlined"
                      hide-details="auto"
                      :items="regions"
                      :rules="[(v) => !!v || 'Region is required']"
                      class="score-entry-field mb-2"
                      required
                    ></v-select>

                    <v-select
                      v-if="hasStates"
                      v-model="selectedState"
                      label="State / Province / Territory (Optional)"
                      placeholder="Select state or province"
                      prepend-inner-icon="mdi-map"
                      variant="outlined"
                      hide-details="auto"
                      :items="statesForCountry"
                      class="score-entry-field mb-2"
                      clearable
                    ></v-select>

                    <v-text-field
                      v-if="selectedCountry && !hasStates"
                      v-model="selectedState"
                      label="State / Province / Territory (Optional)"
                      placeholder="Enter state, province, or territory"
                      prepend-inner-icon="mdi-map"
                      variant="outlined"
                      hide-details="auto"
                      class="score-entry-field mb-2"
                    ></v-text-field>

                    <v-text-field
                      v-if="showCityField"
                      v-model="city"
                      label="City (optional)"
                      placeholder="e.g., San Francisco"
                      prepend-inner-icon="mdi-city"
                      variant="outlined"
                      hide-details="auto"
                      class="score-entry-field mb-2"
                      clearable
                    ></v-text-field>

                    <v-text-field
                      v-if="showNeighbourhoodField"
                      v-model="neighbourhoodPostal"
                      label="Neighbourhood or postal code (optional)"
                      placeholder="e.g., Mission District or 94102"
                      prepend-inner-icon="mdi-mailbox"
                      variant="outlined"
                      hide-details="auto"
                      class="score-entry-field mb-2"
                      clearable
                    ></v-text-field>
                  </div>
                </v-expand-transition>

                <div class="text-center mt-6">
                  <v-btn
                    type="submit"
                    size="large"
                    class="score-entry-cta text-none"
                    :disabled="!canProceedStep1 || loadingProducts || !canAddBrand"
                    :loading="loadingProducts || freePlanBrandLimitPending"
                  >
                    <span v-if="!loadingProducts">Get my Visibility score</span>
                    <span v-else>Analyzing brand...</span>
                    <v-icon v-if="!loadingProducts" end size="small">mdi-arrow-right</v-icon>
                  </v-btn>
                </div>
              </v-form>
            </div>
          </v-card>

          <!--
            Persistent, non-dismissible upgrade banner for free-tier users.
            No close control and no dismissal state by design — it renders on
            every visit and sits between the score-entry form and the tracked
            brands list.
          -->
          <div v-if="showFreePlanBanner" class="free-plan-banner">
            <div class="free-plan-banner__main">
              <span class="free-plan-banner__badge">Free plan</span>
              <div>
                <div class="free-plan-banner__title">You're on the Free plan</div>
                <p class="free-plan-banner__body mb-0">
                  Upgrade to unlock the full Action Center, provider comparisons, and visibility trends over time.
                </p>
              </div>
            </div>
            <div class="free-plan-banner__actions">
              <a
                class="free-plan-banner__link"
                role="button"
                tabindex="0"
                @click="router.push('/profile?tab=billing')"
                @keyup.enter="router.push('/profile?tab=billing')"
              >Compare plans</a>
              <v-btn
                variant="flat"
                rounded="pill"
                class="brand-upgrade-cta-btn text-none font-weight-bold"
                @click="router.push('/profile?tab=billing')"
              >
                Upgrade Your Plan
                <v-icon end size="small">mdi-arrow-right</v-icon>
              </v-btn>
            </div>
          </div>

          <section
            v-if="recentBrandsLoading || decoratedBrands.length > 0"
            class="tracked-brands"
          >
            <div class="tracked-brands__header">
              <div>
                <div class="tracked-brands__title-row">
                  <v-icon color="primary" size="22">mdi-clock-outline</v-icon>
                  <h2 class="brand-heading tracked-brands__title">Recently tracked brands</h2>
                </div>
                <div class="tracked-brands__summary">{{ recentBrandsSummary }}</div>
              </div>

              <div class="tracked-brands__filter">
                <v-icon size="18" class="tracked-brands__filter-icon">mdi-magnify</v-icon>
                <input
                  v-model="brandFilter"
                  type="text"
                  placeholder="Filter by brand, product or place"
                  aria-label="Filter tracked brands"
                />
                <button
                  v-if="brandFilter"
                  type="button"
                  class="tracked-brands__filter-clear"
                  title="Clear"
                  @click="brandFilter = ''"
                >
                  <v-icon size="15">mdi-close</v-icon>
                </button>
              </div>
            </div>

            <div v-if="recentBrandsLoading" class="tracked-brands__grid">
              <v-skeleton-loader
                v-for="i in brandPageSize"
                :key="i"
                type="article"
                class="tracked-brand-card tracked-brand-card--skeleton"
              ></v-skeleton-loader>
            </div>

            <div v-else-if="visibleBrands.length > 0" class="tracked-brands__grid">
              <div
                v-for="brand in visibleBrands"
                :key="brand.id"
                class="tracked-brand-card"
                @click="viewBrandHistory(brand)"
              >
                <div class="tracked-brand-card__top">
                  <span class="tracked-brand-card__name">{{ brand.name }}</span>
                  <span
                    class="tracked-brand-card__chip"
                    :class="brand.pending ? 'is-pending' : 'is-tracking'"
                  >
                    {{ brand.pending ? 'Pending' : 'Tracking' }}
                  </span>
                </div>

                <div class="tracked-brand-card__fill">
                  <div v-if="brand.product" class="tracked-brand-card__product">
                    {{ brand.product }}
                  </div>

                  <div class="tracked-brand-card__place">
                    <v-icon size="16" class="tracked-brand-card__place-icon">mdi-map-marker-outline</v-icon>
                    <div class="tracked-brand-card__place-text">
                      <div class="tracked-brand-card__place-lead">{{ brand.regionLead || '—' }}</div>
                      <div v-if="brand.regionContext" class="tracked-brand-card__place-trail">
                        {{ brand.regionContext }}
                      </div>
                    </div>
                  </div>

                  <div class="tracked-brand-card__score">
                    <div class="tracked-brand-card__score-head">
                      <span class="tracked-brand-card__score-label">Visibility</span>
                      <span
                        class="tracked-brand-card__score-value"
                        :style="{ color: brand.scoreColor }"
                      >{{ brand.scoreLabel }}</span>
                    </div>
                    <div class="tracked-brand-card__bar">
                      <div
                        class="tracked-brand-card__bar-fill"
                        :style="{ width: brand.scoreWidth, background: brand.scoreColor }"
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div v-else-if="brandFilter" class="tracked-brands__empty">
              <v-icon size="28" class="tracked-brands__empty-icon">mdi-map-search-outline</v-icon>
              <div class="tracked-brands__empty-title">No tracked brands match that filter</div>
              <div class="tracked-brands__empty-body">
                Try a shorter term, or run a new Visibility check above.
              </div>
              <v-btn
                variant="outlined"
                rounded="pill"
                class="text-none font-weight-bold mt-4 tracked-brands__empty-btn"
                @click="brandFilter = ''"
              >
                Clear filter
              </v-btn>
            </div>

            <div v-if="canLoadMoreBrands" class="tracked-brands__more">
              <v-btn
                variant="outlined"
                rounded="pill"
                class="text-none font-weight-bold tracked-brands__more-btn"
                @click="brandsShown += brandPageSize"
              >
                <v-icon start>mdi-chevron-down</v-icon>
                Load more brands
              </v-btn>
              <span class="tracked-brands__more-caption">{{ remainingBrandsLabel }}</span>
            </div>
          </section>
        </div>
      </template>

      <!-- Step 2: Clinic service selection -->
      <v-row v-if="currentStep === 2" justify="center" class="score-entry-row">
        <v-col cols="12" md="10" lg="8">
          <div class="text-center mb-6">
            <img :src="inletsLogo" alt="inlets.ai" class="brand-logo-img" />
          </div>
          <v-card variant="flat" class="pa-8 pa-md-10 score-entry-card">
            <div class="text-center mb-8">
              <h1 class="score-entry-heading brand-heading mb-3">
                Services to analyse
              </h1>
              <p class="score-entry-subtext mb-0">
                <span v-if="productCategories.length > 0">
                  Based on your clinic, select the service patients ask AI about:
                </span>
                <span v-else>
                  Manually enter a clinic service to analyse:
                </span>
              </p>
            </div>

            <!-- Loading State -->
            <div v-if="loadingProducts" class="text-center py-12">
              <v-progress-circular indeterminate color="primary" size="64" class="mb-4"></v-progress-circular>
              <div class="text-h6">Analysing your clinic...</div>
              <div class="text-body-2 text-medium-emphasis mt-2">Finding relevant services</div>
            </div>

            <!-- Error State -->
            <div v-else-if="productError" class="text-center py-8">
              <v-icon color="error" size="64" class="mb-4">mdi-alert-circle</v-icon>
              <div class="text-h6 mb-2">Unable to suggest services</div>
              <div class="text-body-2 text-medium-emphasis mb-6">{{ productError }}</div>
              <v-alert type="info" variant="outlined" class="mb-4">
                You can still proceed by manually entering a clinic service below.
              </v-alert>
            </div>

            <!-- No Products Found: hide fallback list, encourage manual entry -->
            <div v-else-if="noProductsFound && !loadingProducts" class="text-center py-8">
              <v-icon color="warning" size="64" class="mb-4">mdi-information</v-icon>
              <div class="text-h6 mb-2">No services found</div>
              <div class="text-body-2 text-medium-emphasis mb-6">
                We couldn't identify services for your clinic. Please manually enter a service below.
              </div>
            </div>

            <!-- Products List: show only when LLM suggestions exist -->
            <v-row v-if="!loadingProducts && productCategories.length > 0" :class="{ 'disabled-products': manualProductActive }">
              <v-col
                v-for="(product, i) in productCategories"
                :key="i"
                cols="12"
                md="6"
                lg="4"
              >
                <v-card
                  :class="['product-card', { 'selected': selectedProducts.includes(product.value) }]"
                  @click="selectProduct(product.value)"
                  variant="outlined"
                >
                  <v-card-item>
                    <v-radio
                      :model-value="selectedProducts.length > 0 ? selectedProducts[0] : null"
                      :value="product.value"
                      :label="product.label"
                      :disabled="manualProductActive"
                      hide-details
                      density="comfortable"
                      @click.stop="selectProduct(product.value)"
                      @change="selectProduct(product.value)"
                    ></v-radio>
                    <div v-if="product.description" class="text-caption text-medium-emphasis mt-2">
                      {{ product.description }}
                    </div>
                  </v-card-item>
                </v-card>
              </v-col>
            </v-row>

            <!-- Custom Product Entry -->
            <v-divider class="my-8"></v-divider>
            <div class="score-entry-form">
              <div class="text-subtitle-1 mb-4">
                Or manually enter a clinic service
              </div>
              <v-text-field
                v-model="customProductName"
                label="Service name"
                placeholder="e.g., Teeth Cleaning"
                prepend-inner-icon="mdi-pencil"
                variant="outlined"
                hide-details="auto"
                class="score-entry-field mb-2"
              ></v-text-field>
              <v-textarea
                v-model="customProductDescription"
                label="Description (optional)"
                placeholder="e.g., Routine cleaning and preventive dental care"
                prepend-inner-icon="mdi-text"
                variant="outlined"
                hide-details="auto"
                class="score-entry-field"
                rows="3"
                auto-grow
              ></v-textarea>
            </div>

            <div class="mt-8 d-flex justify-space-between align-center">
              <v-btn
                variant="text"
                @click="previousStep"
                class="text-none font-weight-bold"
              >
                <v-icon start>mdi-chevron-double-left</v-icon>
                Go Back
              </v-btn>
              <v-btn
                size="large"
                class="score-entry-cta text-none"
                @click="nextStep"
                :disabled="!canProceedStep2"
              >
                Start Analysis
                <v-icon end size="small">mdi-play</v-icon>
              </v-btn>
            </div>
          </v-card>
        </v-col>
      </v-row>

      <!-- Step 3: Real-time Analysis in Progress (full-width layout matches AeoAnalytics) -->
      <v-row v-if="currentStep === 3" class="mb-6">
        <v-col cols="12">
          <AnalysisProgress
            :tasks="analysisTasks"
            :progress="analysisProgress"
            :show-back-button="true"
            :analysis-ready="analysisReady"
            :review-entity-id="currentReviewEntityId"
            :current-processing-stage="currentProcessingStage"
            :personas="currentAssessmentData?.personas || []"
            :queries="currentAssessmentData?.queries || []"
            :persona-profiles="currentAssessmentData?.personas || []"
            :competitors="currentAssessmentData?.competitors || null"
            :needs-competitor-review="needsCompetitorReview"
            @go-back="previousStep"
            @continue="goToAnalytics"
            @view-details="viewFailedAnalysis"
            @competitors-confirmed="handleCompetitorsConfirmed"
            @personas-confirmed="handlePersonasConfirmed"
            @queries-confirmed="handleQueriesConfirmed"
            @edit-personas="handleEditPersonas"
            @provider-questions-done="handleQueriesConfirmed"
            @edit-queries="handleEditPersonas"
          />
        </v-col>
      </v-row>

    </v-container>

    <!-- Debug/Test Controls (admin only) -->
    <v-container v-if="isAdmin" class="py-8">
      <v-row justify="center">
        <v-col cols="12" class="text-center">
          <v-btn
            color="error"
            variant="outlined"
            size="small"
            @click="deleteAllData"
          >
            <v-icon class="mr-2">mdi-delete</v-icon>
            Delete All Review Data (Test)
          </v-btn>
        </v-col>
      </v-row>
    </v-container>
  </v-main>
</template>
  
<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import AnalysisProgress from '@/components/AnalysisProgress.vue'
import { canViewAnalyticsResults, needsCompetitorConfirmation, analysisStageToTaskKey } from '@/utils/assessmentReadiness'
import inletsLogo from '@/assets/images/inlets-logo-color.svg'

const router = useRouter()
const store = useStore()

// Wizard state (1 = visibility entry home, 2 = services, 3 = analysis)
const currentStep = ref(1)
const progressValue = ref(0)

// Step 1: Brand Confirmation
const brandName = ref('')
const brandUrl = ref('')
const selectedCountry = ref(null)
const selectedState = ref(null)
const city = ref('')
const neighbourhoodPostal = ref('')
const brandForm = ref(null)

const regions = [
  'United States',
  'Canada'
]

// States/Provinces data
const statesData = {
  'United States': ['Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming'],
  'Canada': ['Alberta', 'British Columbia', 'Manitoba', 'New Brunswick', 'Newfoundland and Labrador', 'Northwest Territories', 'Nova Scotia', 'Nunavut', 'Ontario', 'Prince Edward Island', 'Quebec', 'Saskatchewan', 'Yukon']
}

// Accept URLs with or without protocol, with or without www
const URL_PATTERN = /^((https?:\/\/)?(www\.)?)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/i

// URL validation rules
const urlRules = [
  v => !!v || 'URL is required',
  v => URL_PATTERN.test(v) || 'Please enter a valid URL (e.g., example.com)'
]

const isBrandUrlValid = computed(() => URL_PATTERN.test((brandUrl.value || '').trim()))

// Function to normalize URL (add https:// if missing)
const normalizeUrl = (url) => {
  if (!url) return url
  url = url.trim()
  if (!/^https?:\/\//i.test(url)) {
    return 'https://' + url
  }
  return url
}

/** Warm WordPress mShots early — first hit is often a placeholder, later hits are the real capture. */
const prefetchWebsiteSnapshot = () => {
  if (!brandUrl.value) return
  const url = normalizeUrl(brandUrl.value)
  const img = new Image()
  img.decoding = 'async'
  img.src = `https://s0.wp.com/mshots/v1/${encodeURIComponent(url)}?w=1280`
}

// Computed properties for geographic hierarchy
const hasStates = computed(() => {
  if (!selectedCountry.value) return false
  return statesData[selectedCountry.value] && statesData[selectedCountry.value].length > 0
})

const statesForCountry = computed(() => {
  if (!selectedCountry.value || !statesData[selectedCountry.value]) return []
  return statesData[selectedCountry.value]
})

const stateIsPicked = computed(
  () => !!(selectedState.value && String(selectedState.value).trim())
)

// Progressive disclosure: reveal each field after the previous required one is filled.
const showWebsiteField = computed(
  () => !!(brandName.value && brandName.value.trim())
)

const showRegionField = computed(
  () => showWebsiteField.value && !!(brandUrl.value && brandUrl.value.trim())
)

// City after a state/province is chosen (optional — still show once country has states UI).
const showCityField = computed(() => {
  if (!showRegionField.value || !selectedCountry.value) return false
  if (hasStates.value) return stateIsPicked.value
  return true
})

const showNeighbourhoodField = computed(
  () => showCityField.value && !!(city.value && city.value.trim())
)

const canProceedStep1 = computed(() => {
  return brandName.value && isBrandUrlValid.value && selectedCountry.value
})

const buildFullRegion = () => {
  const parts = [selectedCountry.value]
  const state = selectedState.value && String(selectedState.value).trim()
  if (state) parts.push(state)
  const c = city.value && city.value.trim()
  if (c) parts.push(c)
  const n = neighbourhoodPostal.value && neighbourhoodPostal.value.trim()
  if (n) parts.push(n)
  return parts.join(' - ')
}

// Watcher to reset dependent fields
watch(selectedCountry, () => {
  selectedState.value = null
  city.value = ''
  neighbourhoodPostal.value = ''
})

watch(selectedState, (val) => {
  if (!val || !String(val).trim()) {
    city.value = ''
    neighbourhoodPostal.value = ''
  }
})

watch(city, (val) => {
  if (!val || !val.trim()) {
    neighbourhoodPostal.value = ''
  }
})

// Step 2: Product Prioritization
const selectedProducts = ref([])
const suggestedProducts = ref([])
const pendingEntityType = ref(null)
const pendingEntityTypeReasoning = ref(null)
const loadingProducts = ref(false)
const productError = ref(null)
const noProductsFound = ref(false)
const customProductName = ref('')
const customProductDescription = ref('')
// Auto-using Gemini (google) for faster performance
const selectedLlmProvider = ref('google')

// LLM Provider Options
const llmProviderOptions = [
  { title: 'ChatGPT (OpenAI)', value: 'openai' },
  { title: 'Gemini (Google)', value: 'google' }
]

// Computed property for product categories
const productCategories = computed(() => {
  if (suggestedProducts.value && suggestedProducts.value.length > 0) {
    return suggestedProducts.value.map(product => ({
      label: product.name,
      value: product.id,
      description: product.description
    }))
  }
  // No fallback categories
  return []
})

// When typing a custom product, clear any radio selection (single source of truth)
watch(customProductName, (val) => {
  if (val && val.trim().length > 0) {
    selectedProducts.value = []
  }
})

// Whether manual product entry is active
const manualProductActive = computed(() => !!(customProductName.value && customProductName.value.trim()))

// Step 2 proceed control: allow exactly one selection either via radio or custom
const selectedProductCount = computed(() => {
  return customProductName.value && customProductName.value.trim() ? 1 : selectedProducts.value.length
})

// LLM provider for persona and query generation is now hardcoded to Gemini (google), so we only need to check product selection
const canProceedStep2 = computed(() => selectedProductCount.value === 1)

// Step 3: Real-time Analysis
const analysisTasks = ref([
  {
    key: 'persona_creation',
    label: 'Creating User Personas',
    status: 'pending',
    message: 'Generating brand-specific user persona...',
    progress: 0
  },
  {
    key: 'queries_generation',
    label: 'Generating Search Queries',
    status: 'pending',
    message: 'Creating relevant search queries...',
    progress: 0
  },
  {
    key: 'query_execution',
    label: 'Running queries as personas',
    status: 'pending',
    message: 'Executing queries and generating responses...',
    progress: 0
  },
  {
    key: 'query_scoring',
    label: 'Scoring Query Results',
    status: 'pending',
    message: 'Analyzing and scoring AI responses...',
    progress: 0
  },
  {
    key: 'review_competitors',
    label: 'Review Competitors',
    status: 'pending',
    message: 'Review competitors from query results...',
    progress: 0
  },
  {
    key: 'competitor_comparison',
    label: 'Comparing Products',
    status: 'pending',
    message: 'Analyzing competitors and comparing products...',
    progress: 0
  }
])

const analysisProgress = ref(0)
const currentTaskIndex = ref(0)
const currentReviewEntityId = ref(null)
const pollingInterval = ref(null)
const currentAssessmentData = ref(null)
const currentProcessingStage = ref(null)
const latestAssessmentSnapshot = ref(null)
const analysisReady = ref(false)

const needsCompetitorReview = computed(() => {
  return needsCompetitorConfirmation(latestAssessmentSnapshot.value)
})

// Admin check for debug controls
const isAdmin = computed(() => store.getters.isAdmin)

// Recently Tracked Brands state
const recentBrands = ref([])
const recentBrandsLoading = ref(false)
const recentBrandsError = ref(null)

// Tracked-brands grid: client-side filter + "load more" pagination.
const brandPageSize = 6
const brandFilter = ref('')
const brandsShown = ref(brandPageSize)

// Reset the visible window whenever the filter changes so results start from the top.
watch(brandFilter, () => {
  brandsShown.value = brandPageSize
})

// Visibility score → brand colour ramp (matches the tracked-brand card bars).
const visibilityColor = (score) => {
  if (score >= 70) return 'var(--brand-teal-500)'
  if (score >= 50) return 'var(--brand-ocean-600)'
  return 'var(--brand-amber-400)'
}

const decorateBrand = (b) => {
  const pending = b.status !== 'completed'
  const score = b.aeoScore
  const hasScore = !pending && typeof score === 'number'
  return {
    ...b,
    product: b.product || null,
    pending,
    scoreLabel: hasScore ? String(score) : '—',
    scoreWidth: hasScore ? `${Math.max(0, Math.min(100, score))}%` : '0%',
    scoreColor: hasScore ? visibilityColor(score) : 'rgba(13, 30, 53, 0.25)',
  }
}

const decoratedBrands = computed(() => recentBrands.value.map(decorateBrand))

const filteredBrands = computed(() => {
  const q = brandFilter.value.trim().toLowerCase()
  if (!q) return decoratedBrands.value
  return decoratedBrands.value.filter((b) =>
    [b.name, b.product, b.region].filter(Boolean).join(' ').toLowerCase().includes(q)
  )
})

const visibleBrands = computed(() => filteredBrands.value.slice(0, brandsShown.value))

const canLoadMoreBrands = computed(() => filteredBrands.value.length > visibleBrands.value.length)

const remainingBrandsLabel = computed(() => {
  const remaining = filteredBrands.value.length - visibleBrands.value.length
  return `${remaining} more ${remaining === 1 ? 'brand' : 'brands'}`
})

const recentBrandsSummary = computed(() => {
  const q = brandFilter.value.trim()
  if (q) {
    const n = filteredBrands.value.length
    return `${n} ${n === 1 ? 'brand matches' : 'brands match'} “${q}”`
  }
  return `Showing ${visibleBrands.value.length} of ${decoratedBrands.value.length} · select a brand to open its analytics`
})

const teamPlanTierKey = computed(() => store.getters.teamPlanTierKey)
/**
 * Free-tier brand-audit cap. Set high enough to be effectively unlimited today;
 * lower it to a real number here to re-introduce a hard cap (one-line change).
 */
const FREE_PLAN_BRAND_LIMIT = 250
/** Free tier (no paid team plan): capped at FREE_PLAN_BRAND_LIMIT brand audits. */
const freePlanBrandCapReached = computed(
  () => teamPlanTierKey.value === 'free' && recentBrands.value.length >= FREE_PLAN_BRAND_LIMIT
)
/** Persistent upgrade banner above the score-entry form — free tier only. */
const showFreePlanBanner = computed(() => teamPlanTierKey.value === 'free')
/** Wait for brand list before showing add CTA on free tier (avoids flash on refresh). */
const freePlanBrandLimitPending = computed(
  () => teamPlanTierKey.value === 'free' && recentBrandsLoading.value
)
const canAddBrand = computed(
  () => !freePlanBrandCapReached.value && !freePlanBrandLimitPending.value
)

// Region is a variable-depth path (1–4 segments joined by " - "), e.g.
// "Worldwide" or "United States - New Jersey - Paramus Park - 07652".
// Split it into the most specific segment (leads) and the broader context beneath.
const splitRegionPath = (region) => {
  const parts = String(region || '')
    .split(' - ')
    .map(s => s.trim())
    .filter(Boolean)
  return {
    lead: parts.length ? parts[parts.length - 1] : '',
    context: parts.slice(0, -1).join(' - '),
  }
}

const mapReviewEntitiesToRecentBrands = (entities) => {
  return (entities || []).map(entity => {
    const { lead: regionLead, context: regionContext } = splitRegionPath(entity.region)
    return {
      id: entity.id,
      name: entity.brand_name,
      url: entity.url,
      product: entity.product_name,
      // `region` comes back on the GET /review_entities index — no backend work needed.
      region: entity.region || '',
      regionLead,
      regionContext,
      // Category Ownership (0.6 × AI Ranking Score + 0.4 × Share of Voice) —
      // same metric and formula as the "Category Ownership" tile in AEO
      // Analytics (backend: CategoryOwnershipCalculator). Not the same as
      // overall_aeo_score, which blends different visibility/consideration
      // category averages.
      aeoScore: typeof entity.latest_assessment?.category_ownership === 'number'
        ? Math.round(entity.latest_assessment.category_ownership)
        : null,
      lastUpdated: entity.latest_assessment?.created_at || entity.created_at,
      // Use latest_assessment status for the pending/completed badge.
      // Backend `GET /review_entities` includes `latest_assessment` for each entity.
      status: entity.latest_assessment?.status || null,
    }
  })
}

const loadRecentBrands = async () => {
  recentBrandsLoading.value = true
  recentBrandsError.value = null

  try {
    const result = await store.dispatch('fetchReviewEntities')

    if (result.success && result.review_entities) {
      recentBrands.value = mapReviewEntitiesToRecentBrands(result.review_entities)
      brandsShown.value = brandPageSize
    } else {
      recentBrands.value = []
      recentBrandsError.value = result.error || 'Failed to load recently tracked brands.'
    }
  } catch (error) {
    console.error('Failed to load recent brands:', error)
    recentBrands.value = []
    recentBrandsError.value = error?.error || 'Failed to load recently tracked brands.'
  } finally {
    recentBrandsLoading.value = false
  }
}

// Methods
const deleteAllData = async () => {
  const confirmed = window.confirm('Are you sure you want to delete ALL review entities and related data? This cannot be undone.');
  if (!confirmed) return;
  try {
    await store.dispatch('deleteAllReviewEntities');
    // No need to clear recentBrands - it's a computed property that will automatically update
    console.log('All review entities deleted.');
  } catch (e) {
    console.error('Failed to delete all review entities:', e);
    alert(e?.error || 'Failed to delete review entities');
  }
}

const nextStep = async () => {
  if (currentStep.value === 1) {
    if (!canAddBrand.value) return

    // Validate Step 1 (Vuetify 3 form.validate() resolves to { valid })
    const { valid } = await brandForm.value.validate()
    if (!valid) return

    // Kick off WordPress mShots as early as possible (URL is now confirmed).
    prefetchWebsiteSnapshot()
    
    // Fetch product suggestions from LLM
    await fetchProductSuggestions()
    
    // Move to step 2
    currentStep.value = 2
    progressValue.value = 66.66
  } else if (currentStep.value === 2) {
    // Persist selected product/brand into ReviewEntity and create bare Assessment
    try {
      const region = buildFullRegion()
      const { productName, productDescription } = getSelectedProductMeta()

      const result = await store.dispatch('createReviewEntity', {
        brand_name: brandName.value,
        url: normalizeUrl(brandUrl.value),
        region,
        product_name: productName,
        product_description: productDescription,
        llm_provider: selectedLlmProvider.value,
        entity_type: pendingEntityType.value,
        entity_type_reasoning: pendingEntityTypeReasoning.value
      })
      
      // Store the created review entity ID for later redirect
      if (result.success && result.review_entity) {
        currentReviewEntityId.value = result.review_entity.id
      }
    } catch (e) {
      console.error('Failed to create review entity:', e)
      // Optional: surface UI feedback here
    }

    // Start Step 3 - Analysis
    currentStep.value = 3
    progressValue.value = 100
    await runAnalysis()
  }
}

const fetchProductSuggestions = async () => {
  loadingProducts.value = true
  productError.value = null
  noProductsFound.value = false
  suggestedProducts.value = []
  pendingEntityType.value = null
  pendingEntityTypeReasoning.value = null

  try {
    // Build brand information string
    const cityLine = city.value?.trim() ? `City: ${city.value.trim()}\n` : ''
    const neighbourhoodLine = neighbourhoodPostal.value?.trim()
      ? `Neighbourhood/Postal: ${neighbourhoodPostal.value.trim()}\n`
      : ''
    const brandInfo = `
Brand Name: ${brandName.value}
Website: ${brandUrl.value}
Region: ${selectedCountry.value}
${selectedState.value ? `State/Province: ${selectedState.value}\n` : ''}${cityLine}${neighbourhoodLine}`.trim()

    const result = await store.dispatch('suggestProducts', brandInfo)

    pendingEntityType.value = result.entity_type || result.products?.entity_type || null
    pendingEntityTypeReasoning.value = result.entity_type_reasoning || result.products?.entity_type_reasoning || null
    
    if (result.success && result.products) {
      const products = result.products.products || result.products
      
      if (products && products.length > 0) {
        suggestedProducts.value = products
      } else {
        noProductsFound.value = true
      }
    } else {
      noProductsFound.value = true
      productError.value = result.error || 'Failed to get product suggestions'
    }
  } catch (error) {
    console.error('Error fetching products:', error)
    productError.value = error.response?.data?.error || 'An error occurred while fetching product suggestions'
    noProductsFound.value = true
  } finally {
    loadingProducts.value = false
  }
}

const previousStep = () => {
  if (currentStep.value === 2) {
    currentStep.value = 1
    progressValue.value = 0
    // Reset product selection state when going back
    suggestedProducts.value = []
    pendingEntityType.value = null
    pendingEntityTypeReasoning.value = null
    selectedProducts.value = []
    customProductName.value = ''
    customProductDescription.value = ''
    productError.value = null
    noProductsFound.value = false
    selectedLlmProvider.value = 'google'
  }
}

const selectProduct = (productValue) => {
  if (manualProductActive.value) return
  // Only allow one product selection
  if (selectedProducts.value.includes(productValue)) {
    // If clicking the same product, don't deselect (keep it selected)
    return
  }
  selectedProducts.value = [productValue]
  // Clear any custom product input when choosing a suggested/default product
  customProductName.value = ''
  customProductDescription.value = ''
}

const getSelectedProductMeta = () => {
  if (customProductName.value && customProductName.value.trim()) {
    return {
      productName: customProductName.value.trim(),
      productDescription: (customProductDescription.value || '')
    }
  }
  const selectedId = selectedProducts.value[0]
  const match = suggestedProducts.value.find(p => p.id === selectedId)
  return {
    productName: match ? (match.name || match.label || '') : '',
    productDescription: match && match.description ? match.description : ''
  }
}

const updateTasksFromStage = (processingStage, status, assessment = null) => {
  currentProcessingStage.value = processingStage
  if (assessment) {
    latestAssessmentSnapshot.value = assessment
  }
  
  if (status === 'completed') {
    // All tasks completed
    analysisTasks.value.forEach(task => {
      task.status = 'completed'
    })
    analysisProgress.value = 100
    return
  }
  
  if (status === 'failed') {
    const taskKey = analysisStageToTaskKey(processingStage, assessment)
    const stageIndex = taskKey ? analysisTasks.value.findIndex(t => t.key === taskKey) : 0
    analysisTasks.value.forEach((task, index) => {
      if (index < stageIndex) {
        task.status = 'completed'
      } else if (index === stageIndex) {
        task.status = 'failed'
        task.message = 'Processing failed'
      } else {
        task.status = 'pending'
      }
    })
    return
  }
  
  // Handle pending status with processing_stage
  if (!processingStage) {
    // No processing stage yet, all tasks should be pending
    analysisTasks.value.forEach(task => {
      task.status = 'pending'
    })
    analysisProgress.value = 0
    return
  }
  
  const taskKey = analysisStageToTaskKey(processingStage, assessment)
  const stageIndex = taskKey ? analysisTasks.value.findIndex(t => t.key === taskKey) : -1
  
  if (stageIndex >= 0) {
    analysisTasks.value.forEach((task, index) => {
      if (index < stageIndex) {
        task.status = 'completed'
      } else if (index === stageIndex) {
        if (task.key === 'review_competitors' || processingStage?.startsWith('confirm_')) {
          task.status = 'action-required'
          if (task.key === 'review_competitors' || processingStage === 'confirm_competitors') {
            task.message = 'Review the competitors most mentioned in your query responses below'
          } else if (processingStage === 'confirm_personas') {
            task.message = 'Please review and confirm the generated personas below'
          } else if (processingStage === 'confirm_queries') {
            task.message = 'Please review and confirm the generated queries below'
          } else if (processingStage === 'confirm_provider_queries') {
            task.message = 'Please review the provider questions below'
          }
        } else {
          task.status = 'in-progress'
          const messages = {
            'persona_creation': 'Creating customer personas...',
            'queries_generation': 'Generating search queries as customer personas...',
            'query_execution': 'Running queries as personas and generating responses...',
            'query_scoring': 'Scoring brand performance...',
            'competitor_comparison': 'Comparing products with competitors...'
          }
          task.message = messages[task.key] || task.message
        }
      } else {
        task.status = 'pending'
      }
    })
    
    // Update progress based on completed stages
    const completedCount = analysisTasks.value.filter(t => t.status === 'completed').length
    analysisProgress.value = (completedCount / analysisTasks.value.length) * 100
  }
}

const pollAssessmentStatus = async () => {
  if (!currentReviewEntityId.value) {
    console.error('No review entity ID to poll')
    return
  }

    try {
      const result = await store.dispatch('fetchReviewEntity', currentReviewEntityId.value)
      
      if (result.success && result.data?.latest_assessment) {
        const assessment = result.data.latest_assessment
        const { status, processing_stage } = assessment
        
        // Get comparison data for competitors
        const comparison = assessment.comparison || null
        
        // Store assessment data for confirmation UI + scan finale
        currentAssessmentData.value = {
          personas: assessment.personas || [],
          queries: result.data.queries || [],
          competitors: comparison?.competitor_data || null
        }
      
      updateTasksFromStage(processing_stage, status, assessment)
      
      // Stop polling if we're in a confirmation stage (requires user input)
      if (needsCompetitorConfirmation(assessment) || processing_stage === 'confirm_personas' || processing_stage === 'confirm_queries' || processing_stage === 'confirm_provider_queries') {
        if (pollingInterval.value) {
          clearInterval(pollingInterval.value)
          pollingInterval.value = null
        }
        return
      }
      
      // If grounded results are ready (or fully completed), show Continue → Analytics.
      // Scan animation plays on AeoAnalytics, not on the Dashboard wizard.
      if (canViewAnalyticsResults(assessment)) {
        if (pollingInterval.value) {
          clearInterval(pollingInterval.value)
          pollingInterval.value = null
        }
        analysisReady.value = true
      } else if (status === 'failed') {
        // Stop polling on failure but don't redirect - let user see the error
        if (pollingInterval.value) {
          clearInterval(pollingInterval.value)
          pollingInterval.value = null
        }
      }
    }
  } catch (error) {
    console.error('Failed to poll assessment status:', error)
    // Continue polling even on error, but could add retry logic here
  }
}

const handleCompetitorsConfirmed = async () => {
  // Refresh assessment status immediately after confirmation
  // The backend updates the stage immediately when confirming
  await pollAssessmentStatus()
  
  // Resume polling since we're now in the next processing stage
  if (!needsCompetitorReview.value && currentProcessingStage.value !== 'confirm_personas' && currentProcessingStage.value !== 'confirm_queries' && currentProcessingStage.value !== 'confirm_provider_queries') {
    if (!pollingInterval.value && currentReviewEntityId.value) {
      pollingInterval.value = setInterval(pollAssessmentStatus, 3000)
    }
  }
}

const handlePersonasConfirmed = async () => {
  // Refresh assessment status immediately after confirmation
  // The backend updates the stage immediately when confirming
  await pollAssessmentStatus()
  
  // Resume polling since we're now in queries_generation stage
  if (!needsCompetitorReview.value && currentProcessingStage.value !== 'confirm_personas' && currentProcessingStage.value !== 'confirm_queries' && currentProcessingStage.value !== 'confirm_provider_queries') {
    if (!pollingInterval.value && currentReviewEntityId.value) {
      pollingInterval.value = setInterval(pollAssessmentStatus, 3000)
    }
  }
}

const handleQueriesConfirmed = async () => {
  // Refresh assessment status immediately after confirmation
  // The backend updates the stage immediately when confirming
  await pollAssessmentStatus()
  
  // Resume polling since we're now in query_execution stage
  if (currentProcessingStage.value !== 'confirm_personas' && currentProcessingStage.value !== 'confirm_queries' && currentProcessingStage.value !== 'confirm_provider_queries') {
    if (!pollingInterval.value && currentReviewEntityId.value) {
      pollingInterval.value = setInterval(pollAssessmentStatus, 3000)
    }
  }
}

const handleEditPersonas = async () => {
  // Refresh assessment status to show persona confirmation UI
  await pollAssessmentStatus()
}

const runAnalysis = async () => {
  if (!currentReviewEntityId.value) {
    console.error('No review entity ID for analysis')
    return
  }

  // Warm website mShot while the pipeline runs (helps Analytics scan animation).
  prefetchWebsiteSnapshot()
  analysisReady.value = false
  
  // Start polling every 3 seconds
  pollingInterval.value = setInterval(pollAssessmentStatus, 3000)
  
  // Do an immediate poll to get initial status
  await pollAssessmentStatus()
}

const viewBrandHistory = (brand) => {
  router.push({ path: '/analytics', query: { review_entity_id: brand.id } })
}

const viewFailedAnalysis = () => {
  if (currentReviewEntityId.value) {
    router.push({ path: '/analytics', query: { review_entity_id: currentReviewEntityId.value } })
  } else {
    router.push('/analytics')
  }
}

const goToAnalytics = () => {
  if (currentReviewEntityId.value) {
    router.push({
      path: '/analytics',
      query: { review_entity_id: currentReviewEntityId.value, auto_scan: '1' }
    })
  } else {
    router.push('/analytics')
  }
}

const getScoreColor = (score) => {
  if (score == null) return 'grey'
  if (score >= 90) return 'success'
  if (score >= 80) return 'primary'
  if (score >= 70) return 'warning'
  return 'error'
}

// Lifecycle hook
onMounted(() => {
  loadRecentBrands()
})

// Cleanup polling interval on component unmount
onBeforeUnmount(() => {
  if (pollingInterval.value) {
    clearInterval(pollingInterval.value)
    pollingInterval.value = null
  }
})

// Watch for step changes
watch(currentStep, (newStep, oldStep) => {
  // No need to refetch - recentBrands is a computed property that auto-updates from store
  
  // Clean up polling when leaving step 3
  if (oldStep === 3 && newStep !== 3) {
    if (pollingInterval.value) {
      clearInterval(pollingInterval.value)
      pollingInterval.value = null
    }
    analysisReady.value = false
    // Reset analysis state
    analysisTasks.value.forEach(task => {
      task.status = 'pending'
    })
    analysisProgress.value = 0
    currentTaskIndex.value = 0
  }
})

// Reset wizard when navigating back to dashboard
watch(() => router.currentRoute.value.path, (newPath) => {
  if (newPath === '/dashboard' && currentStep.value !== 1) {
    // Clean up polling if active
    if (pollingInterval.value) {
      clearInterval(pollingInterval.value)
      pollingInterval.value = null
    }
    
    // Reset wizard state when returning to dashboard
    currentStep.value = 1
    progressValue.value = 0
    // Reset all form fields
    brandName.value = ''
    brandUrl.value = ''
    selectedCountry.value = null
    selectedState.value = null
    city.value = ''
    neighbourhoodPostal.value = ''
    selectedProducts.value = []
    customProductName.value = ''
    customProductDescription.value = ''
    suggestedProducts.value = []
    pendingEntityType.value = null
    pendingEntityTypeReasoning.value = null
    productError.value = null
    noProductsFound.value = false
    selectedLlmProvider.value = 'google'
    currentReviewEntityId.value = null
    
    // Reset analysis state
    analysisReady.value = false
    analysisTasks.value.forEach(task => {
      task.status = 'pending'
    })
    analysisProgress.value = 0
    currentTaskIndex.value = 0
  }

  if (newPath === '/dashboard') {
    loadRecentBrands()
  }
})
</script>
  
<style scoped>
.cursor-pointer {
  cursor: pointer;
}

.cursor-pointer:hover {
  background-color: rgba(0, 0, 0, 0.04);
}

.v-card {
  border-radius: 16px;
}

.text-decoration-none {
  text-decoration: none;
}

.v-btn {
  text-transform: none;
  border-radius: 100px;
}

.v-text-field {
  margin-bottom: 16px;
}

.score-entry-row {
  min-height: auto;
  align-items: flex-start;
  padding-top: 2rem;
}

/* ---------- Step 1 home layout ---------- */
.dashboard-home {
  max-width: 1180px;
  margin: 0 auto;
  padding-top: 0.5rem;
}

.dashboard-home__logo {
  display: flex;
  align-items: center;
  padding: 4px 0 20px;
}

.score-entry-inner {
  max-width: 34rem;
  margin-inline: auto;
}

.score-entry-eyebrow {
  display: block;
  margin-bottom: 14px;
}

/* ---------- Free plan banner (dark) ---------- */
.free-plan-banner {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 20px 32px;
  margin-top: 24px;
  padding: 22px 28px;
  border-radius: 16px;
  background: var(--brand-ink-950);
  color: #fff;
  overflow: hidden;
}

.free-plan-banner__main {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  min-width: 0;
}

.free-plan-banner__badge {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  height: 24px;
  padding: 0 10px;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(233, 168, 74, 0.45);
  color: var(--brand-amber-400);
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.free-plan-banner__title {
  font-size: 1.05rem;
  font-weight: 700;
  line-height: 1.4;
  color: #fff;
}

.free-plan-banner__body {
  margin-top: 6px;
  font-size: 0.875rem;
  line-height: 1.5;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.6);
  max-width: 46rem;
}

.free-plan-banner__actions {
  display: flex;
  align-items: center;
  gap: 24px;
  flex-shrink: 0;
}

.free-plan-banner__link {
  color: rgba(255, 255, 255, 0.75);
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}

.free-plan-banner__link:hover {
  color: #fff;
}

/* ---------- Recently tracked brands ---------- */
.tracked-brands {
  margin-top: 36px;
}

.tracked-brands__header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 20px;
  flex-wrap: wrap;
  margin-bottom: 16px;
}

.tracked-brands__title-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.tracked-brands__title {
  font-size: 1.35rem;
  font-weight: 600;
  margin: 0;
}

.tracked-brands__summary {
  font-size: 0.8125rem;
  color: rgba(13, 30, 53, 0.5);
  margin-top: 6px;
}

.tracked-brands__filter {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  padding: 0 8px 0 12px;
  border: 1px solid var(--brand-sand-300);
  border-radius: 12px;
  background: #fff;
  box-sizing: border-box;
  min-width: 288px;
}

.tracked-brands__filter-icon {
  color: rgba(13, 30, 53, 0.45);
}

.tracked-brands__filter input {
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 0.875rem;
  color: var(--brand-ink-950);
}

.tracked-brands__filter input::placeholder {
  color: rgba(13, 30, 53, 0.42);
}

.tracked-brands__filter-clear {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border: none;
  border-radius: 9999px;
  background: var(--brand-sand-100);
  color: rgba(13, 30, 53, 0.55);
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease;
}

.tracked-brands__filter-clear:hover {
  background: var(--brand-ocean-100);
  color: var(--brand-ocean-600);
}

.tracked-brands__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 16px;
}

.tracked-brand-card {
  display: flex;
  flex-direction: column;
  border: 1px solid var(--brand-sand-300);
  border-radius: 16px;
  background: #fff;
  padding: 20px;
  cursor: pointer;
  box-shadow: 0 1px 3px rgba(18, 32, 53, 0.06);
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
}

.tracked-brand-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(18, 32, 53, 0.12);
  border-color: var(--brand-ocean-600);
}

.tracked-brand-card--skeleton {
  cursor: default;
  min-height: 220px;
}

.tracked-brand-card--skeleton:hover {
  transform: none;
  box-shadow: 0 1px 3px rgba(18, 32, 53, 0.06);
  border-color: var(--brand-sand-300);
}

.tracked-brand-card__top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
}

.tracked-brand-card__name {
  font-size: 1rem;
  font-weight: 600;
  line-height: 1.35;
  color: var(--brand-ink-950);
}

.tracked-brand-card__chip {
  display: inline-flex;
  align-items: center;
  height: 24px;
  padding: 0 10px;
  border-radius: 6px;
  flex-shrink: 0;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.tracked-brand-card__chip.is-tracking {
  background: #e2f5f5;
  color: #12868a;
}

.tracked-brand-card__chip.is-pending {
  background: var(--brand-amber-100);
  color: #8b5f16;
}

.tracked-brand-card__fill {
  display: flex;
  flex-direction: column;
  margin-top: auto;
}

.tracked-brand-card__product {
  align-self: flex-start;
  margin-top: 14px;
  height: 26px;
  padding: 0 12px;
  display: inline-flex;
  align-items: center;
  border-radius: 9999px;
  background: var(--brand-ocean-100);
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--brand-ocean-600);
}

.tracked-brand-card__place {
  display: flex;
  align-items: flex-start;
  gap: 7px;
  margin: 12px 0 16px;
}

.tracked-brand-card__place-icon {
  color: rgba(13, 30, 53, 0.4);
  flex-shrink: 0;
  margin-top: 1px;
}

.tracked-brand-card__place-text {
  min-width: 0;
}

.tracked-brand-card__place-lead {
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1.35;
  color: #122035;
}

.tracked-brand-card__place-trail {
  font-size: 0.75rem;
  line-height: 1.4;
  color: rgba(13, 30, 53, 0.5);
  margin-top: 2px;
}

.tracked-brand-card__score {
  padding-top: 14px;
  border-top: 1px solid var(--brand-sand-100);
}

.tracked-brand-card__score-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 8px;
}

.tracked-brand-card__score-label {
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: rgba(13, 30, 53, 0.45);
}

.tracked-brand-card__score-value {
  font-family: 'Lora', serif;
  font-size: 1.375rem;
  font-weight: 600;
  letter-spacing: -0.02em;
}

.tracked-brand-card__bar {
  height: 5px;
  border-radius: 9999px;
  background: var(--brand-sand-100);
  overflow: hidden;
}

.tracked-brand-card__bar-fill {
  height: 100%;
  border-radius: 9999px;
  transition: width 0.5s ease;
}

.tracked-brands__empty {
  border: 1px dashed var(--brand-sand-300);
  border-radius: 16px;
  background: var(--brand-sand-100);
  padding: 40px 24px;
  text-align: center;
}

.tracked-brands__empty-icon {
  color: rgba(13, 30, 53, 0.35);
}

.tracked-brands__empty-title {
  font-family: 'Lora', serif;
  font-size: 1.125rem;
  font-weight: 600;
  color: #122035;
  margin-top: 10px;
}

.tracked-brands__empty-body {
  font-size: 0.875rem;
  color: rgba(13, 30, 53, 0.55);
  margin-top: 6px;
}

.tracked-brands__empty-btn.v-btn {
  border-color: rgba(33, 105, 168, 0.45);
  color: var(--brand-ocean-600);
}

.tracked-brands__more {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  margin-top: 24px;
}

.tracked-brands__more-btn.v-btn {
  border-color: var(--brand-sand-300);
  color: var(--brand-ocean-600);
}

.tracked-brands__more-caption {
  font-size: 0.75rem;
  color: rgba(13, 30, 53, 0.45);
}

.score-entry-card {
  border-radius: 24px !important;
  background: #fff;
  box-shadow: 0 8px 32px rgba(13, 30, 53, 0.08);
  border: 1px solid var(--brand-sand-300);
}

.score-entry-heading {
  font-size: clamp(1.75rem, 4vw, 2.35rem);
  font-weight: 600;
  line-height: 1.25;
  color: var(--brand-ink-950);
}

.score-entry-brand {
  font-family: 'Lora', serif;
  font-style: italic;
  font-weight: 500;
  color: var(--brand-ocean-600);
}

.score-entry-subtext {
  font-size: 0.95rem;
  line-height: 1.45;
  color: rgba(13, 30, 53, 0.55);
  max-width: 28rem;
  margin-inline: auto;
}

.score-entry-reveal {
  padding-top: 8px;
}

.score-entry-form :deep(.score-entry-field .v-field) {
  border-radius: 14px;
  background: #fff;
}

.score-entry-form :deep(.score-entry-field .v-field__outline) {
  --v-field-border-opacity: 0.35;
}

.score-entry-form :deep(.score-entry-field .v-field__outline__start),
.score-entry-form :deep(.score-entry-field .v-field__outline__end) {
  border-color: var(--brand-sand-300);
}

.score-entry-cta.v-btn {
  background-color: var(--brand-amber-400) !important;
  color: var(--brand-upgrade-cta-text) !important;
  border-radius: 9999px !important;
  font-weight: 600 !important;
  letter-spacing: normal !important;
  box-shadow: none !important;
  padding-inline: 28px !important;
  min-height: 48px;
  opacity: 1;
}

.score-entry-cta.v-btn:hover:not(:disabled) {
  filter: brightness(1.06);
}

.score-entry-cta.v-btn:disabled {
  background-color: rgba(233, 168, 74, 0.28) !important;
  color: rgba(61, 42, 0, 0.4) !important;
  opacity: 1 !important;
}

.product-card {
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
  border-radius: 16px;
}

.product-card:hover {
  background-color: var(--brand-ocean-100);
  border-color: var(--brand-ocean-600) !important;
  transform: translateY(-2px);
}

.product-card.selected {
  background-color: var(--brand-ocean-100);
  border-color: var(--brand-ocean-600) !important;
  /* Keep border width constant to avoid layout shift; add visual emphasis via shadow */
  box-shadow: 0 0 0 2px rgba(33, 105, 168, 0.25);
}

.disabled-products {
  opacity: 0.5;
  pointer-events: none;
}
</style>